import { useNavigate, useParams } from "react-router-dom";

import { API_ROUTES } from "@/config/apiRoutes";
import { getPagePath, PAGES } from "@/config/pages";
import { isSupportedLanguage } from "@/i18n/routing";
import { useAuthStore } from "@/stores/AuthStore";
import { useCapabilityStore } from "@/stores/CapabilityStore";
import { useOrganisationStore } from "@/stores/OrganisationStore";
import { loggerService, LogTag } from "@/Utils/LoggerService";
import appFetch from "@/Utils/Services/Fetch/appFetch";

/**
 * Centralizes the organisation-user logout flow shared by the navbar actions
 * and the sidebar logout button: best-effort server-side cookie revocation,
 * local store cleanup, and a redirect to the localized login page.
 */
const useLogout = (): (() => Promise<void>) => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const clearUser = useAuthStore((state) => state.clearUser);
  const clearOrganisation = useOrganisationStore(
    (state) => state.clearOrganisation,
  );
  const clearCapabilities = useCapabilityStore((state) => state.clear);

  return async () => {
    // Best-effort: revoke the refresh cookie + clear the auth cookies server-side.
    await appFetch(API_ROUTES.auth.logout, { method: "POST" });

    clearUser();
    clearOrganisation();
    // Otherwise the next account to sign in on this browser starts with the
    // previous organisation's plan, and sees its badges until the reload.
    clearCapabilities();
    loggerService.info(LogTag.AUTH, "User logged out");

    const loginPath = isSupportedLanguage(lang)
      ? getPagePath("login", lang)
      : PAGES.login;
    navigate(loginPath, { replace: true });
  };
};

export default useLogout;
